import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Link } from 'react-router';
import Video from 'componentsV2/Video';
import useBreakpoint from 'hooks/useBreakpoint';
import { isMobileBreakpoint } from 'helpers/BreakpointHelper';
import { getDynamicImage, COLLECTION_HERO_IMAGE } from 'helpers/ImageHelper';
import { immersiveShowcaseEnabled } from 'helpers/ConfigHelper';
import style from './HeroBackground.module.scss';

const HeroContainer = ({ children, isPromo, theme, url, onClick, item }) => (
  <Link to={url} onClick={onClick} item={item}>
    <section
      className={classNames(style.heroContainer, theme.heroContainer, {
        [(style.heroPromo, theme.heroPromo)]: isPromo,
        [(style.isImmersive, theme.isImmersive)]: immersiveShowcaseEnabled(),
      })}
    >
      {children}
    </section>
  </Link>
);

HeroContainer.propTypes = {
  children: PropTypes.node,
  isPromo: PropTypes.bool,
  item: PropTypes.shape({}),
  onClick: PropTypes.func,
  theme: PropTypes.shape({}).isRequired,
  url: PropTypes.string,
};

const HeroLeftFader = ({ render, theme }) => {
  if (!render) return null;
  return <div className={classNames(style.leftFade, theme.leftFade)} />;
};

HeroLeftFader.propTypes = {
  theme: PropTypes.shape({}).isRequired,
  render: PropTypes.bool,
};

const HeroParallax = ({ children, isParallax, theme }) => {
  useEffect(() => {
    if (isParallax) {
      const backgroundImage = document.getElementById('heroParallax');
      let opacity = 1;
      let transformY;

      const opacityChange = () => {
        const yScrollPosition = window.scrollY;
        const currScrollPos2 =
          window.scrollY || document.documentElement.scrollTop || document.body.scrollTop || 0;
        if (currScrollPos2 <= 500) {
          opacity = (-currScrollPos2 * 0.75) / 500 + 1;
        } else {
          opacity = 0.25;
        }

        transformY = yScrollPosition * 0.2;

        const css = (element, styles) => {
          const el = element;
          Object.keys(styles).forEach(key => {
            el.style[key] = styles[key];
          });
        };

        const setTransformY = `translateY(${transformY}px)`;
        css(backgroundImage, { opacity, transform: setTransformY });
        requestAnimationFrame(opacityChange);
      };
      window.addEventListener('scroll', opacityChange);
      return () => {
        window.removeEventListener('scroll', opacityChange);
      };
    }
    return () => {};
  }, [isParallax]);
  if (!isParallax) return children;
  return (
    <div className={classNames(style.parallax, theme.parallax)} id="heroParallax">
      {children}
    </div>
  );
};

HeroParallax.propTypes = {
  children: PropTypes.node,
  isParallax: PropTypes.bool,
  theme: PropTypes.shape({}).isRequired,
};

const HeroImage = ({ alt, image, theme, unoptimized }) => {
  const breakpoint = useBreakpoint();
  const img = getDynamicImage({
    breakpoint,
    url: image,
    context: COLLECTION_HERO_IMAGE,
    unoptimized,
  });
  return (
    <img
      alt={`${alt}-poster`}
      className={classNames(style.heroVideoImage, theme.heroVideoImage)}
      src={img}
    />
  );
};

HeroImage.propTypes = {
  alt: PropTypes.string,
  image: PropTypes.string.isRequired,
  theme: PropTypes.shape({}).isRequired,
  unoptimized: PropTypes.bool,
};

const HeroVideo = ({ alt, id, image, theme, video, unoptimized }) => {
  const breakpoint = useBreakpoint();
  const img = getDynamicImage({
    breakpoint,
    url: image,
    context: COLLECTION_HERO_IMAGE,
    unoptimized,
  });
  return (
    <Video
      autoPlay
      loop
      muted
      alt={`${alt}-sizzle`}
      className={classNames(style.heroVideoImage, theme.heroVideoImage)}
      id={id}
      poster={img}
      src={video}
    />
  );
};

HeroVideo.propTypes = {
  alt: PropTypes.string,
  id: PropTypes.string.isRequired,
  image: PropTypes.string.isRequired,
  theme: PropTypes.shape({}).isRequired,
  unoptimized: PropTypes.bool,
  video: PropTypes.string,
};

const HeroBottomFader = ({ render, theme }) => {
  if (!render) return null;
  const fadeClasses = [
    style.bottomFade,
    theme.bottomFade,
    style.redesignFade,
    theme.redesignFade,
    style.isSeries,
    theme.isSeries,
  ];
  return <div className={classNames(fadeClasses)} />;
};

HeroBottomFader.propTypes = {
  theme: PropTypes.shape({}).isRequired,
  render: PropTypes.bool,
};

const HeroInfo = ({ theme, children }) => (
  <div className={classNames(style.heroInfo, theme.heroInfo)}>{children}</div>
);

HeroInfo.propTypes = {
  children: PropTypes.node,
  theme: PropTypes.shape({}).isRequired,
};

const HeroBackground = ({
  alt,
  children,
  image,
  isPromo,
  isSeries,
  item,
  onClick,
  renderBottomFader = true,
  renderLeftFader = true,
  sizzle,
  theme,
  unoptimized,
  url,
  videoId,
}) => {
  const breakpoint = useBreakpoint();
  const isMobile = isMobileBreakpoint(breakpoint);
  const imageUrl = isMobile ? image.mobile : image.desktop;

  return (
    <HeroContainer
      isPromo={isPromo}
      isSeries={isSeries}
      item={item}
      onClick={onClick}
      theme={theme}
      url={url}
    >
      <HeroParallax isParallax={isSeries && !isMobile} theme={theme}>
        <HeroImage
          alt={alt}
          image={imageUrl}
          isParallax={isSeries}
          theme={theme}
          unoptimized={unoptimized}
        />
        {!isMobile && (
          <HeroVideo
            alt={alt}
            id={videoId}
            image={imageUrl}
            theme={theme}
            video={sizzle}
            unoptimized={unoptimized}
          />
        )}
        <HeroLeftFader render={renderLeftFader} theme={theme} />
        <HeroBottomFader render={isSeries || (!isMobile && renderBottomFader)} theme={theme} />
      </HeroParallax>
      <HeroInfo theme={theme}>{children}</HeroInfo>
    </HeroContainer>
  );
};

HeroBackground.propTypes = {
  alt: PropTypes.string,
  children: PropTypes.node,
  image: PropTypes.shape({}),
  isPromo: PropTypes.bool,
  isSeries: PropTypes.bool,
  item: PropTypes.shape({}),
  onClick: PropTypes.func,
  renderBottomFader: PropTypes.bool,
  renderLeftFader: PropTypes.bool,
  sizzle: PropTypes.string,
  theme: PropTypes.shape({}).isRequired,
  unoptimized: PropTypes.bool,
  url: PropTypes.string,
  videoId: PropTypes.string,
};

export default HeroBackground;
